import { Camelized, camelizeKeys } from "humps";
import { camelize } from "humps";
import orderBy from "lodash/orderBy";
import partition from "lodash/partition";
import take from "lodash/take";
import { ProfileCustomValuePill } from "PFApp/components/profile_custom_value/profile_custom_value_pill";
import { ParentChildMap } from "PFApp/helpers/get_parent_child_skills_map";
import { useCustomValueTags } from "PFApp/hooks/use_custom_value_tags";
import { useExperienceConfig } from "PFApp/hooks/use_experience_config";
import { useExperienceLabelMap } from "PFApp/hooks/use_experience_label_map";
import { CustomValuePill } from "PFComponents/custom_value_pill";
import { isRankable } from "PFCore/helpers/custom_type";
import { requiresApproval, useCustomValueToString } from "PFCore/helpers/custom_value";
import { useCustomTypes } from "PFCore/helpers/use_custom_types";
import { ActivityPureCustomValue, Id, MatchCustomValue, PureCustomValue } from "PFTypes";
import { ReactNode } from "react";

import { CustomValuesListCommonProps, DEFAULT_LIMIT } from "../custom_values_list.consts";
import css from "../custom_values_list.module.scss";
import { useCustomValuesVisibility } from "../hooks/use_custom_values_visibility";
import { CustomValuesDivider } from "../parts/custom_values_divider";
import { CustomValuesWrapper } from "../parts/custom_values_wrapper";
import { getCustomValueParentChildInfo } from "./profile_custom_values_list.utils";

export type ProfileCustomValuesListProps = CustomValuesListCommonProps & {
  customValues: (MatchCustomValue | ActivityPureCustomValue | PureCustomValue)[];
  profileId: Id;
  experienceTooltipDisabled?: boolean;
  showLeftIcon?: boolean;
  renderCustomValueLeftContent?: (customValueId: Id) => ReactNode;
  renderCustomValueRightContent?: (customValueId: Id) => ReactNode;
  onMoreLessButtonClick?: VoidFunction;
  onMissingClick?: (id: Id, globalId?: Id | null) => void;
  isUnbiasedMode?: boolean;
  allowPreview?: boolean;
  parentChildMap?: ParentChildMap;
};

export const ProfileCustomValuesList = ({
  customFieldName,
  customValues,
  profileId,
  maxNameLength,
  limit = DEFAULT_LIMIT,
  dark = false,
  experienceTooltipDisabled = false,
  canEdit = false,
  vertical = false,
  showLeftIcon = true,
  renderCustomValueLeftContent,
  renderCustomValueRightContent,
  onMissingClick,
  onMoreLessButtonClick,
  isUnbiasedMode = false,
  allowPreview = true,
  parentChildMap
}: ProfileCustomValuesListProps) => {
  const { customTypes } = useCustomTypes();
  const { experienceLevels } = useExperienceConfig();
  const experienceLabelMap = useExperienceLabelMap();
  const { getCustomValueTags } = useCustomValueTags();
  const { getCustomValueString } = useCustomValueToString();

  const [availableCustomValues, missingCustomValues] = partition(
    customValues,
    ({ experience }: MatchCustomValue) => experience !== undefined
  ) as [MatchCustomValue[], (ActivityPureCustomValue | PureCustomValue)[]];

  const sortedAvailableCustomValues = orderBy(
    availableCustomValues,
    [({ top }) => Boolean(top), "value"],
    ["desc", "asc"]
  );

  const sortedMissingCustomValues = orderBy(
    missingCustomValues,
    [
      (customValue) => {
        const { hasChildValues, hasParentValues } = getCustomValueParentChildInfo(
          customValue,
          parentChildMap
        );
        return hasParentValues || hasChildValues;
      },
      "value"
    ],
    ["desc", "asc"]
  );

  const {
    visibleAvailableCustomValuesCount,
    visibleMissingCustomValuesCount,
    showAll,
    displayShowMoreButton,
    toggleShowAll
  } = useCustomValuesVisibility({
    availableCustomValuesCount: sortedAvailableCustomValues.length,
    missingCustomValuesCount: sortedMissingCustomValues.length,
    limit
  });

  const handleMoreLessButtonClick = () => {
    toggleShowAll();
    onMoreLessButtonClick?.();
  };

  const customType = customTypes.find(({ name }) => camelize(name) === camelize(customFieldName));
  const isCustomTypeRankable = Boolean(customType && isRankable(customType));

  return (
    <CustomValuesWrapper
      displayShowMoreButton={displayShowMoreButton}
      showAll={showAll}
      dark={dark}
      limit={limit}
      vertical={vertical}
      onMoreLessButtonClick={handleMoreLessButtonClick}
    >
      {take(sortedAvailableCustomValues, visibleAvailableCustomValuesCount).map((customValue, index) => {
        const isLastCustomValue = index === visibleAvailableCustomValuesCount - 1;
        const shouldSeparateMissingCustomValuesList =
          isLastCustomValue && visibleMissingCustomValuesCount > 0;

        return (
          <span className={css.customValueWithDivider} key={customValue.id}>
            <ProfileCustomValuePill
              customValue={customValue}
              customType={customType!}
              profileId={profileId}
              maxNameLength={maxNameLength}
              canEdit={canEdit}
              isUnbiasedMode={isUnbiasedMode}
              showLeftIcon={showLeftIcon}
              theme={dark ? "dark" : "light"}
              showTooltip={!experienceTooltipDisabled}
              allowPreview={allowPreview}
              customLeftContent={renderCustomValueLeftContent?.(customValue.id)}
              customRightContent={renderCustomValueRightContent?.(customValue.id)}
            />
            {(!isLastCustomValue || shouldSeparateMissingCustomValuesList) && !vertical && (
              <CustomValuesDivider />
            )}
          </span>
        );
      })}
      {take(sortedMissingCustomValues, visibleMissingCustomValuesCount).map((customValue, index) => {
        const isLastCustomValue = index === visibleMissingCustomValuesCount - 1;
        // TODO: [PROF-5746] Remove "camelizeKeys" when all component usages get camelized customValues
        const camelizedCustomValue = camelizeKeys(customValue) as Camelized<
          ActivityPureCustomValue | PureCustomValue
        >;
        const isPendingApproval = requiresApproval(customType!, camelizedCustomValue);
        const { requiredExperience } = camelizedCustomValue as Camelized<ActivityPureCustomValue>;
        const { hasChildValues, hasParentValues } = getCustomValueParentChildInfo(
          camelizedCustomValue,
          parentChildMap
        );

        return (
          <span className={css.customValueWithDivider} key={camelizedCustomValue.id}>
            <CustomValuePill
              name={getCustomValueString(camelizedCustomValue)}
              requiredExperience={showLeftIcon && isCustomTypeRankable ? requiredExperience : undefined}
              experienceLevels={experienceLevels}
              experienceLabelMap={experienceLabelMap}
              customTypeAriaLabel={customType?.display_as}
              isPendingApproval={isPendingApproval}
              hasChildValues={hasChildValues}
              hasParentValues={hasParentValues}
              tags={getCustomValueTags(camelizedCustomValue.tagIds)}
              maxNameLength={maxNameLength}
              theme={dark ? "dark" : "light"}
              showTooltip={isCustomTypeRankable && !experienceTooltipDisabled}
              customLeftIconName={
                (!isCustomTypeRankable || !requiredExperience) && showLeftIcon ? "cross" : undefined
              }
              customLeftContent={renderCustomValueLeftContent?.(camelizedCustomValue.id)}
              customRightContent={renderCustomValueRightContent?.(camelizedCustomValue.id)}
              onClick={
                onMissingClick
                  ? () => onMissingClick?.(camelizedCustomValue.id, camelizedCustomValue.globalId)
                  : undefined
              }
            />
            {!isLastCustomValue && !vertical && <CustomValuesDivider />}
          </span>
        );
      })}
    </CustomValuesWrapper>
  );
};
